import React from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ShopifyColorSchemeSet } from '../../utils/brandColorSchemes';
import PresetCamera, { PresetCameraKind } from './PresetCamera';

export interface StorefrontMockPreviewProps {
  presetId: string;
  /** The merchant's store name — not the preset name. */
  storeName: string;
  logoSrc?: string;
  schemes: ShopifyColorSchemeSet;
}

/**
 * Mock storefront so the merchant sees their derived Dawn schemes before
 * downloading the zip.
 *
 * One composition per preset, echoing that preset's gallery screenshot: the
 * card shows the look in example colours, this shows the same look in the
 * merchant's colours. Same structure, different palette.
 *
 * EVERY colour-bearing surface comes from `schemes` (derived by
 * `deriveColorSchemes`). Nothing is hardcoded — that is what makes the preview
 * agree with the downloaded theme. The only exception is the product drawings,
 * which stand in for photography and stay neutral.
 *
 * Scheme roles, matching the preset bundles:
 *   scheme-1  white page      header, body, product grids, primary buttons
 *   scheme-2  soft brand tint image wells, Classic bands and cards, Mono tiles
 *   scheme-3  brand colour    Studio hero
 *   scheme-4  near-black      footers, Pro Shop announcement bar
 *   scheme-5  accent          Pro Shop promo strip and Sale badge
 *
 * Fonts: the app only loads Inter, so each preset uses a generic stack that
 * preserves the distinction that actually reads at preview size — serif vs
 * sans vs grotesque — rather than the exact families the theme ships
 * (Playfair/Crimson, Assistant, Work Sans). Swap in the real families here if
 * they are ever added to the app's font loading.
 */

const SERIF_STACK = 'Georgia, "Times New Roman", serif';
const SANS_STACK = 'Inter, system-ui, -apple-system, sans-serif';
const GROTESQUE_STACK = 'Helvetica, Arial, sans-serif';

/** Hairlines and faint borders derive from the surface's own text colour. */
const line = (scheme: { text: string }, opacity = 0.16) => alpha(scheme.text, opacity);

const Wordmark: React.FC<{
  storeName: string;
  logoSrc?: string;
  sx?: object;
}> = ({ storeName, logoSrc, sx }) => {
  if (logoSrc) {
    return (
      <Box
        component="img"
        src={logoSrc}
        alt=""
        sx={{ height: 24, objectFit: 'contain', display: 'block' }}
      />
    );
  }
  return <Box sx={sx}>{storeName}</Box>;
};

type Product = { name: string; price: string; spec?: string; badge?: string; kind: PresetCameraKind };

const STUDIO_PRODUCTS: Product[] = [
  { name: 'α7 IV Body', price: '$2,498', kind: 'mirrorless' },
  { name: 'X100VI', price: '$1,599', kind: 'compact' },
  { name: 'GR IIIx', price: '$1,199', kind: 'compact' },
  { name: '24-70 f/2.8', price: '$1,099', kind: 'lens' },
];

const PRO_SHOP_PRODUCTS: Product[] = [
  { name: 'Sony α7 IV Body', spec: '33MP · 4K60 · IBIS', price: '$2,498', kind: 'mirrorless' },
  { name: 'Canon EOS R6 II', spec: '24MP · 40fps · IBIS', price: '$2,099', badge: 'Sale', kind: 'mirrorless' },
  { name: 'Fujifilm X100VI', spec: '40MP · fixed 23mm', price: '$1,599', kind: 'compact' },
  { name: 'Sigma 24-70 f/2.8', spec: 'E-mount · 835g', price: '$1,099', kind: 'lens' },
];

const CLASSIC_PRODUCTS: Product[] = [
  { name: 'Rangefinder 35', price: '$640', kind: 'rangefinder' },
  { name: 'Instant Wide', price: '$149', kind: 'instant' },
  { name: 'Portrait 50mm f/2', price: '$320', kind: 'lens' },
];

const MONO_PRODUCTS: Product[] = [
  { name: 'X100 Silver', price: '$1,599', kind: 'rangefinder' },
  { name: 'Q3 Black', price: '$5,995', kind: 'mirrorless' },
  { name: 'GR IIIx', price: '$1,199', kind: 'compact' },
];

const StorefrontMockPreview: React.FC<StorefrontMockPreviewProps> = ({
  presetId,
  storeName,
  logoSrc,
  schemes,
}) => {
  const s1 = schemes['scheme-1'];
  const s2 = schemes['scheme-2'];
  const s3 = schemes['scheme-3'];
  const s4 = schemes['scheme-4'];
  const s5 = schemes['scheme-5'];

  const frame = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    overflow: 'hidden',
  } as const;

  // ── Studio: editorial, full-bleed hero ──
  if (presetId === 'studio') {
    return (
      <Box sx={{ ...frame, bgcolor: s1.background, color: s1.text, fontFamily: SANS_STACK }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 4,
            py: 1.75,
            fontSize: 13,
          }}
        >
          <Wordmark
            storeName={storeName}
            logoSrc={logoSrc}
            sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}
          />
          <Box sx={{ display: 'flex', gap: 3, opacity: 0.85 }}>
            <span>Cameras</span><span>Lenses</span><span>Lighting</span><span>Journal</span>
          </Box>
          <Box sx={{ opacity: 0.85 }}>Cart (0)</Box>
        </Box>

        <Box
          sx={{
            bgcolor: s3.background,
            color: s3.text,
            minHeight: 170,
            px: 6,
            py: 3.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ maxWidth: 440 }}>
            <Box sx={{ fontSize: 12, letterSpacing: '.14em', opacity: 0.85 }}>NEW SEASON</Box>
            <Box sx={{ fontSize: 38, fontWeight: 600, lineHeight: 1.05, my: 1.25 }}>
              See the world in full frame
            </Box>
            <Box
              sx={{
                display: 'inline-block',
                px: 2.75,
                py: 1.125,
                fontSize: 14,
                bgcolor: s3.button,
                color: s3.button_label,
                border: `1px solid ${s3.button}`,
              }}
            >
              Shop cameras
            </Box>
          </Box>
          <PresetCamera kind="mirrorless" width={240} rotate={-6} />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 3,
            px: 4,
            py: 3.5,
          }}
        >
          {STUDIO_PRODUCTS.map((product) => (
            <Box key={product.name}>
              <Box
                sx={{
                  height: 110,
                  bgcolor: s2.background,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <PresetCamera kind={product.kind} width={120} />
              </Box>
              <Box sx={{ fontSize: 14 }}>{product.name}</Box>
              <Box sx={{ fontSize: 13, opacity: 0.75 }}>{product.price}</Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ bgcolor: s4.background, color: s4.text, px: 4, py: 1.5, fontSize: 12 }}>
          © {storeName}
        </Box>
      </Box>
    );
  }

  // ── Pro Shop: dense, spec-forward. No footer — density is the point. ──
  if (presetId === 'pro-shop') {
    return (
      <Box sx={{ ...frame, bgcolor: s1.background, color: s1.text, fontFamily: SANS_STACK }}>
        <Box
          sx={{
            bgcolor: s4.background,
            color: s4.text,
            px: 2,
            py: 0.625,
            fontSize: 11,
            textAlign: 'center',
          }}
        >
          Free next-day shipping over $99 · Price match guarantee
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.25,
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${line(s1)}`,
          }}
        >
          <Wordmark storeName={storeName} logoSrc={logoSrc} sx={{ fontSize: 15, fontWeight: 600 }} />
          <Box
            sx={{
              flex: 1,
              height: 30,
              border: `1px solid ${line(s1)}`,
              borderRadius: '2px',
              display: 'flex',
              alignItems: 'center',
              px: 1,
              fontSize: 12,
              opacity: 0.7,
            }}
          >
            Search by model, mount or spec
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, fontSize: 12, fontWeight: 500 }}>
            <span>Mirrorless</span><span>Lenses</span><span>Video</span><span>Used</span>
          </Box>
          <Box
            sx={{
              bgcolor: s1.button,
              color: s1.button_label,
              borderRadius: '2px',
              px: 1.25,
              py: 0.5,
              fontSize: 12,
            }}
          >
            Cart
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: s5.background,
            color: s5.text,
            px: 2,
            py: 1.125,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ fontSize: 13, fontWeight: 600 }}>
            Trade-in week — up to $400 off a new body
          </Box>
          <Box
            sx={{
              bgcolor: s5.button,
              color: s5.button_label,
              borderRadius: '2px',
              px: 1.25,
              py: 0.5,
              fontSize: 12,
              whiteSpace: 'nowrap',
            }}
          >
            See offers
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 1.25,
            px: 2,
            py: 1.75,
          }}
        >
          {PRO_SHOP_PRODUCTS.map((product) => (
            <Box
              key={product.name}
              sx={{
                border: `1px solid ${line(s1)}`,
                borderRadius: '2px',
                p: 1.25,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: 84,
                  bgcolor: s2.background,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PresetCamera kind={product.kind} width={96} />
                {product.badge && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      left: 4,
                      bgcolor: s5.background,
                      color: s5.text,
                      fontSize: 10,
                      fontWeight: 600,
                      borderRadius: '2px',
                      px: 0.625,
                    }}
                  >
                    {product.badge}
                  </Box>
                )}
              </Box>
              <Box sx={{ fontSize: 13, fontWeight: 600 }}>{product.name}</Box>
              <Box sx={{ fontSize: 11, opacity: 0.75 }}>{product.spec}</Box>
              <Box sx={{ fontSize: 15, fontWeight: 600 }}>{product.price}</Box>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    fontSize: 11,
                    px: 1,
                    py: 0.375,
                    border: `1px solid ${s1.text}`,
                    borderRadius: '2px',
                  }}
                >
                  Compare
                </Box>
                <Box
                  sx={{
                    fontSize: 11,
                    px: 1,
                    py: 0.375,
                    bgcolor: s1.button,
                    color: s1.button_label,
                    borderRadius: '2px',
                  }}
                >
                  Add
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // ── Classic: heritage serif, narrow measure, pill buttons ──
  if (presetId === 'classic') {
    return (
      <Box sx={{ ...frame, bgcolor: s1.background, color: s1.text, fontFamily: SERIF_STACK }}>
        <Box sx={{ borderBottom: `1px solid ${line(s1)}` }}>
          <Box
            sx={{
              maxWidth: 780,
              mx: 'auto',
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              px: 3,
              py: 1.75,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, fontSize: 15 }}>
              <span>Film</span><span>Instant</span><span>Lenses</span>
            </Box>
            <Wordmark storeName={storeName} logoSrc={logoSrc} sx={{ fontSize: 21, fontWeight: 700 }} />
            <Box sx={{ textAlign: 'right', fontSize: 15 }}>Our story · Cart</Box>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: s2.background,
            color: s2.text,
            px: 3,
            py: 3.25,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.25,
            textAlign: 'center',
          }}
        >
          <Box sx={{ fontSize: 13, letterSpacing: '.14em' }}>EST. 1964</Box>
          <Box sx={{ fontSize: 30, fontWeight: 700 }}>Since the darkroom era</Box>
          <Box
            sx={{
              px: 2.75,
              py: 1,
              fontSize: 16,
              borderRadius: '40px',
              border: `2px solid ${s2.button}`,
              bgcolor: s2.button,
              color: s2.button_label,
            }}
          >
            Browse film cameras
          </Box>
        </Box>

        <Box sx={{ maxWidth: 780, mx: 'auto', px: 3, py: 2.75 }}>
          <Box sx={{ fontSize: 18, textAlign: 'center', mb: 1.5 }}>Featured</Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
            {CLASSIC_PRODUCTS.map((product) => (
              <Box
                key={product.name}
                sx={{
                  bgcolor: s2.background,
                  color: s2.text,
                  borderRadius: 3,
                  p: '6px 6px 12px',
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    height: 100,
                    bgcolor: s1.background,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <PresetCamera kind={product.kind} width={112} />
                </Box>
                <Box sx={{ fontSize: 16, fontWeight: 600 }}>{product.name}</Box>
                <Box sx={{ fontSize: 14 }}>{product.price}</Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: s4.background,
            color: s4.text,
            textAlign: 'center',
            py: 1.75,
            fontSize: 14,
          }}
        >
          {storeName} · Our story · All products
        </Box>
      </Box>
    );
  }

  // ── Mono: near-chromeless, 2px gutters, small type ──
  return (
    <Box sx={{ ...frame, bgcolor: s1.background, color: s1.text, fontFamily: GROTESQUE_STACK }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          px: 3.5,
          py: 1.5,
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '.08em',
        }}
      >
        <Box>Shop</Box>
        <Wordmark storeName={storeName} logoSrc={logoSrc} sx={{ fontSize: 13, letterSpacing: '.3em' }} />
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', alignItems: 'center' }}>
          <Box sx={{ border: `1px solid ${line(s1, 0.6)}`, px: 1, py: 0.5 }}>Shop all</Box>
          <Box>Bag 0</Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: '2px',
          px: 3.5,
        }}
      >
        {MONO_PRODUCTS.map((product) => (
          <Box
            key={product.name}
            sx={{
              height: 200,
              bgcolor: s2.background,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PresetCamera kind={product.kind} width={150} />
            <Box
              sx={{
                position: 'absolute',
                left: 10,
                right: 10,
                bottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 11,
                letterSpacing: '.04em',
              }}
            >
              <span>{product.name}</span>
              <span>{product.price}</span>
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: 3.5,
          py: 1.75,
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '.08em',
        }}
      >
        <span>© {storeName}</span>
        <span>Instagram · Newsletter</span>
      </Box>
    </Box>
  );
};

export default StorefrontMockPreview;
